import { Injectable, inject } from '@angular/core';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';

type NestedObject = {
  [key: string]: any;
};

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private readonly fileTypeIconMap: Record<string, IconProp> = {
    'MS Word': ['fas', 'file-word'],
    PDF: ['fas', 'file-pdf'],
    HTML: ['fas', 'code'],
    'MS Excel': ['fas', 'file-xls'],
    CSV: ['fas', 'file-csv'],
  };

  private static readonly PERIOD_FIELDS = new Set([
    'post_time_published',
    'post_time_created',
    '_post_audit_time_created',
  ]);

  protected toastService = inject(ToastService);
  protected translateService = inject(TranslateService);
  constructor() {}

  reorderArray<T>(
    array: T[],
    previousIndex: number,
    currentIndex: number,
  ): T[] {
    if (
      previousIndex < 0 ||
      previousIndex >= array.length ||
      currentIndex < 0 ||
      currentIndex >= array.length
    ) {
      throw new Error('Invalid index');
    }

    // Create a copy of the array
    const newArray = [...array];

    // Remove the item from the previous index
    const [item] = newArray.splice(previousIndex, 1);

    // Insert the item at the new index
    newArray.splice(currentIndex, 0, item);

    return newArray;
  }

  /**
   * Creates a debounced function that delays invoking the provided function until after wait milliseconds have elapsed since the last time it was invoked.
   * @param func The function to debounce.
   * @param wait The number of milliseconds to delay.
   * @returns A debounced function.
   */
  debounce(func, wait) {
    let timeout;

    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Formats a number by adding spaces every three digits.
   *
   * @param number - The number to be formatted.
   * @returns The formatted number with spaces.
   */
  formatNumberWithSpaces = (number: number | string | null | undefined) => {
    return (number ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  /**
   * Converts an object with array keys to a nested object.
   * @param obj - The object with array keys to be converted.
   * @returns The converted nested object.
   */
  convertArrayKeysToNested(obj: { [key: string]: number }): NestedObject {
    const result: NestedObject = {};

    Object.entries(obj).forEach(([key, value]) => {
      const parts = key.split(/[\[\]]+/).filter((part) => part !== '');
      let currentLevel = result;

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          currentLevel[part] = value;
        } else {
          if (!currentLevel[part]) {
            currentLevel[part] = {};
          }
          currentLevel = currentLevel[part];
        }
      });
    });

    return result;
  }

  /**
   * Converts the keys of an object to snake case.
   * @param obj - The object to convert.
   * @returns The object with keys converted to snake case.
   */
  convertToSnakeCase(obj: any): any {
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      result[snakeCaseKey] = value;
    }
    return result;
  }

  /**
   * Checks if an object is iterable.
   *
   * @param obj - The object to check.
   * @returns A boolean value indicating whether the object is iterable.
   * @typeParam T - The type of elements in the iterable.
   */
  isIterable<T>(obj: any): obj is Iterable<T> {
    return obj != null && typeof obj[Symbol.iterator] === 'function';
  }

  /**
   * Parses an "HH:mm" time string into a native Date (time-of-day on today's
   * date). Returns null for missing/invalid input.
   */
  timeStringToDate(time: string | null | undefined): Date | null {
    if (!time || typeof time !== 'string') {
      return null;
    }

    const parts = time.split(':');
    if (parts.length !== 2) {
      return null;
    }

    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);

    if (
      isNaN(hour) ||
      hour < 0 ||
      hour > 23 ||
      isNaN(minute) ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    return date;
  }

  /**
   * Converts a native Date's time-of-day to the `{ hour, minute }` shape the
   * backend's schedule API expects on submit (it converts the array to a string
   * server-side via convertTimeArrayToString). Returns null for a missing date.
   */
  dateToHm(
    date: Date | null | undefined,
  ): { hour: number; minute: number } | null {
    if (!date) {
      return null;
    }
    return { hour: date.getHours(), minute: date.getMinutes() };
  }

  /**
   * Converts a Unix timestamp (seconds) to a native Date.
   * Returns null for falsy/invalid timestamps.
   */
  unixToDate(unixTimestamp: number | null | undefined): Date | null {
    if (!unixTimestamp || isNaN(unixTimestamp)) {
      return null;
    }
    const date = new Date(unixTimestamp * 1000);
    return date.getTime() > 0 ? date : null;
  }

  /**
   * Converts a native Date to the `{ year, month, day }` shape the backend's
   * schedule / company API expects on submit (month is 1-based). Returns null
   * for a missing date.
   */
  dateToYmd(
    date: Date | null | undefined,
  ): { year: number; month: number; day: number } | null {
    if (!date) {
      return null;
    }
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  deepCopy(obj, hash = new WeakMap()) {
    // Check if obj is a reference type
    if (obj === null || typeof obj !== 'object') return obj;

    // Handle Date
    if (obj instanceof Date) return new Date(obj);

    // Handle Array
    if (obj instanceof Array) {
      return obj.reduce((arr, item, i) => {
        arr[i] = this.deepCopy(item, hash);
        return arr;
      }, []);
    }

    // Handle circular references
    if (hash.has(obj)) return hash.get(obj);

    // Handle Objects
    const result = {};
    hash.set(obj, result);
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] = this.deepCopy(obj[key], hash);
      }
    }
    return result;
  }

  getFileTypeIcon(fileType: string): IconProp {
    return this.fileTypeIconMap[fileType] || ['far', 'file'];
  }

  downloadFile(fileName: string): string {
    const baseurl = environment.apiBaseUrl;
    const url = `${baseurl}/utils/download_file?file=${fileName}`;

    return url;
  }

  async copyToClipboard(value: string): Promise<void> {
    if (!value) {
      return Promise.reject('Nothing to copy');
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        this.execCommandCopyFallback(value);
      }
      this.toastService.showSuccess(
        this.translateService.instant(
          'utils.copy-to-clipboard.successfully-copied-to-clipboard',
        ),
      );
    } catch {
      // ! Clipboard API can reject when the document isn't focused (e.g. inside
      // ! a freshly opened dialog) or when permissions are denied — fall back
      // ! to the legacy execCommand path before giving up.
      try {
        this.execCommandCopyFallback(value);
        this.toastService.showSuccess(
          this.translateService.instant(
            'utils.copy-to-clipboard.successfully-copied-to-clipboard',
          ),
        );
      } catch {
        this.toastService.showError(
          this.translateService.instant(
            'utils.copy-to-clipboard.failed-to-copy-to-clipboard',
          ),
        );
        throw new Error('Copy to clipboard failed');
      }
    }
  }

  private execCommandCopyFallback(value: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    let successful = false;
    try {
      successful = document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }

    if (!successful) {
      throw new Error('execCommand copy returned false');
    }
  }

  convertFormDataI18nKeys(formData: Record<string, any>) {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(formData)) {
      // both control-name conventions in use: `title_i18n.language_id_6`
      // (query editor, workspace) and `title_i18n.6` (dashboard)
      const match = key.match(/^([\w]+_i18n)\.(?:language_id_)?(\d+)$/);

      if (match) {
        const [, baseKey, langId] = match;

        if (!result[baseKey]) {
          result[baseKey] = {};
        }

        result[baseKey][langId] = value;
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  static isPeriodField(fieldName: string): boolean {
    return UtilsService.PERIOD_FIELDS.has(fieldName);
  }

  getFileNameFromDisposition(
    disposition: string | null,
    fallback = 'export.csv',
  ): string {
    if (!disposition) return fallback;

    // filename*=UTF-8''...  (RFC 5987)
    const mStar = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (mStar?.[1]) return decodeURIComponent(mStar[1].replace(/(^"|"$)/g, ''));

    // filename="..."
    const m = disposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
    return m?.[2] ?? fallback;
  }

  downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
